import { memo, useEffect, useRef } from 'react'
import { FOCUS_RING_INSET } from '@/lib/focus-ring'
import { cn } from '@/lib/utils'
import { CheckIcon, MinusIcon } from '@phosphor-icons/react'

interface TableSelectCheckboxProps {
  item: { type: string; id: string }
  isSelected: boolean
  isIndeterminate?: boolean
  onSelectionChange: (
    id: string,
    selected: boolean,
    withRange?: boolean,
  ) => void
  showForGroups?: boolean
  className?: string
}

function TableSelectCheckboxComponent({
  item,
  isSelected,
  isIndeterminate = false,
  onSelectionChange,
  showForGroups = false,
  className,
}: TableSelectCheckboxProps) {
  const lastShiftRef = useRef(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = isIndeterminate
    }
  }, [isIndeterminate])

  // Only show checkbox for non-group items by default
  if (item.type === 'group' && !showForGroups) {
    return (
      <div
        className={`flex h-6 w-6 items-center justify-center ${className || ''}`}
      >
        <div className="h-4 w-4" />
      </div>
    )
  }

  return (
    <div
      className={`flex h-6 w-6 items-center justify-center ${className || ''}`}
    >
      <span className="relative flex h-5 w-5 items-center justify-center">
        <input
          ref={inputRef}
          type="checkbox"
          checked={isSelected && !isIndeterminate}
          aria-checked={isIndeterminate ? 'mixed' : isSelected}
          className={cn(
            'transition-tonal peer h-5 w-5 shrink-0 appearance-none border-0 bg-transparent duration-150',
            FOCUS_RING_INSET,
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          onPointerDown={e => e.stopPropagation()}
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation()
            lastShiftRef.current = e.shiftKey
          }}
          onChange={e => {
            const selected = isIndeterminate ? true : e.target.checked
            onSelectionChange(item.id, selected, lastShiftRef.current)
          }}
        />
        <span
          className={cn(
            'transition-tonal pointer-events-none absolute inset-0 border-2 border-border bg-background shadow-sm duration-150 peer-hover:border-primary/60',
            (isSelected || isIndeterminate) &&
              'border-primary bg-primary text-primary-foreground',
          )}
        />
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-primary-foreground">
          {isIndeterminate ? (
            <MinusIcon className="h-3.5 w-3.5 stroke-2" />
          ) : (
            <CheckIcon
              className={cn(
                'h-3.5 w-3.5 stroke-2',
                isSelected ? 'opacity-100' : 'opacity-0',
              )}
            />
          )}
        </span>
      </span>
    </div>
  )
}

export const TableSelectCheckbox = memo(
  TableSelectCheckboxComponent,
  (prev, next) =>
    prev.item.id === next.item.id &&
    prev.item.type === next.item.type &&
    prev.isSelected === next.isSelected &&
    prev.isIndeterminate === next.isIndeterminate &&
    prev.showForGroups === next.showForGroups &&
    prev.className === next.className &&
    prev.onSelectionChange === next.onSelectionChange,
)

interface TableSelectHeaderProps {
  selectedCount: number
  totalCount?: number
  onSelectAll: () => void
  onSelectNone: () => void
  selectAllLabel?: string
  selectNoneLabel?: string
  className?: string
}

function TableSelectHeaderComponent({
  selectedCount,
  totalCount = 0,
  onSelectAll,
  onSelectNone,
  className,
}: TableSelectHeaderProps) {
  const allSelected = totalCount > 0 && selectedCount === totalCount
  const someSelected = selectedCount > 0 && selectedCount < totalCount
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = someSelected
    }
  }, [someSelected])

  const handleCheckedChange = () => {
    if (allSelected) {
      // Fully checked → deselect all
      onSelectNone()
    } else {
      // Unchecked or indeterminate → select all
      onSelectAll()
    }
  }

  return (
    <div
      className={`flex h-6 w-6 items-center justify-center ${className || ''}`}
      onClick={e => e.stopPropagation()}
      onPointerDown={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
    >
      <span className="relative flex h-5 w-5 items-center justify-center">
        <input
          ref={inputRef}
          type="checkbox"
          checked={allSelected}
          aria-checked={someSelected ? 'mixed' : allSelected}
          className={cn(
            'transition-tonal peer h-5 w-5 shrink-0 appearance-none border-0 bg-transparent duration-150',
            FOCUS_RING_INSET,
            'disabled:cursor-not-allowed disabled:opacity-50',
          )}
          onChange={handleCheckedChange}
        />
        <span
          className={cn(
            'transition-tonal pointer-events-none absolute inset-0 border-2 border-border bg-background shadow-sm duration-150 peer-hover:border-primary/60',
            (allSelected || someSelected) &&
              'border-primary bg-primary text-primary-foreground',
          )}
        />
        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-primary-foreground">
          {someSelected ? (
            <MinusIcon className="h-3.5 w-3.5 stroke-2" />
          ) : (
            <CheckIcon
              className={cn(
                'h-3.5 w-3.5 stroke-2',
                allSelected ? 'opacity-100' : 'opacity-0',
              )}
            />
          )}
        </span>
      </span>
    </div>
  )
}

export const TableSelectHeader = memo(TableSelectHeaderComponent)
